import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, X, ArrowDownUp, ArrowUp, ArrowDown, Filter, Clock, PencilLine } from "lucide-react";
import { DealersToolbar } from "./dealers/DealersToolbar";
import { DealerTagBadges } from "@/components/DealerTagBadges";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

const BATCH_SIZE = 100;
const COLUMN_FILTER_DEBOUNCE_MS = 300;
const SITE_STATUSES = ["no_site", "offline", "online", "disabled", "site_deleted"];

function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// Same MHQ role set as the /dealers route itself (App.js) — never the Dealer role.

const SITE_STATUS_BADGE_VARIANT = {
  no_site: "outline",
  offline: "secondary",
  online: "default",
  disabled: "secondary",
  site_deleted: "destructive",
};

function SiteStatusBadge({ dealer, t }) {
  if (dealer.cms_status_change_pending) {
    return (
      <Badge variant="outline" className="gap-1 whitespace-nowrap text-muted-foreground">
        <Clock className="h-3 w-3" />
        {t(`dealership.siteStatus.${dealer.cms_site_status_pending}`)}
      </Badge>
    );
  }
  const status = dealer.cms_site_status || "no_site";
  return (
    <Badge variant={SITE_STATUS_BADGE_VARIANT[status]} className="whitespace-nowrap">
      {t(`dealership.siteStatus.${status}`)}
    </Badge>
  );
}

function ColumnFilter({ label, active, onOpenChange, children, testId }) {
  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          className={`inline-flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-background/60 ${
            active ? "text-primary" : "text-muted-foreground"
          }`}
          aria-label={label}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className="h-3 w-3" strokeWidth={2.25} />
          {active && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-primary" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" onClick={(e) => e.stopPropagation()}>
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** Single-choice list inside a ColumnFilter popover; "" means no filter. */
function ChoiceFilter({ value, onChange, options, testId }) {
  return (
    <div className="flex flex-col gap-0.5">
      {[{ value: "", label: "All" }, ...options].map((o) => (
        <button
          key={o.value || "all"}
          data-testid={`${testId}-${o.value || "all"}`}
          onClick={() => onChange(o.value)}
          className={`rounded px-2 py-1 text-left text-sm hover:bg-muted ${
            value === o.value ? "bg-muted font-medium text-primary" : ""
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SortIndicator({ active, dir }) {
  if (!active) return <ArrowDownUp className="h-3 w-3 text-muted-foreground/60" />;
  return dir === "asc" ? (
    <ArrowUp className="h-3 w-3 text-primary" />
  ) : (
    <ArrowDown className="h-3 w-3 text-primary" />
  );
}

/** Left/ctrl/cmd-click uses client-side routing in the same tab; middle-click
 * (auxclick, button 1) or ctrl/cmd+click opens the dealer in a new tab —
 * `<tr>` has no native href to make that happen for free like a real <a>. */
function openDealerOnClick(navigate, dealerId) {
  return (e) => {
    const url = `/dealers/${dealerId}`;
    if (e.button === 1 || e.metaKey || e.ctrlKey) {
      e.preventDefault();
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(url);
  };
}

export default function DealersList() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const canBulkUpdate = hasGlobalPermission(auth.user, PERM.NETWORK_DEALERS_READ);
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const sentinelRef = useRef(null);

  // Top-of-page filters
  const [q, setQ] = useState(params.get("q") || "");
  const [predefined, setPredefined] = useState(
    params.get("missing_coords")
      ? "missing-coords"
      : params.get("activation_id")
        ? "activation"
        : "none",
  );
  const [activationId, setActivationId] = useState(params.get("activation_id") || "");
  const [activations, setActivations] = useState([]);
  const [selectedServices, setSelectedServices] = useState(
    (params.get("service_types") || params.get("service_type") || "")
      .split(",")
      .filter(Boolean),
  );
  const [selectedTags, setSelectedTags] = useState(
    (params.get("tags") || "").split(",").filter(Boolean),
  );
  const [tagMatchMode, setTagMatchMode] = useState(params.get("tags_match") === "any" ? "any" : "all");
  const [tagOptions, setTagOptions] = useState({ contract: [], services: [], certifications: [], vz: [] });
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "active");

  // Column-header filters and sorting. Both are applied by the server so they
  // cover every matching dealer, not just the batches scrolled in so far.
  const [codeFilter, setCodeFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [ddmFilter, setDdmFilter] = useState(""); // "" | "yes" | "no"
  const [siteStatusFilter, setSiteStatusFilter] = useState(""); // "" | a SITE_STATUSES value
  const debouncedCode = useDebouncedValue(codeFilter.trim(), COLUMN_FILTER_DEBOUNCE_MS);
  const debouncedName = useDebouncedValue(nameFilter.trim(), COLUMN_FILTER_DEBOUNCE_MS);
  const debouncedCity = useDebouncedValue(cityFilter.trim(), COLUMN_FILTER_DEBOUNCE_MS);

  const [sort, setSort] = useState({ col: "name", dir: "asc" });

  // Bulk selection
  const [selected, setSelected] = useState(new Set());

  // Bumped by every fresh query so responses to superseded ones (fast typing,
  // repeated sort clicks, a scroll batch in flight) are dropped, not shown.
  const requestSeq = useRef(0);

  const buildParams = (extra = {}) => {
    const p = { ...extra, sort: sort.col, order: sort.dir };
    if (q) p.q = q;
    if (predefined === "missing-coords") p.missing_coords = true;
    if (predefined === "activation" && activationId) p.activation_id = activationId;
    if (selectedServices.length === 1) p.service_type = selectedServices[0];
    if (selectedTags.length > 0) p.tags = selectedTags.join(",");
    if (selectedTags.length > 1 && tagMatchMode === "any") p.tags_match = "any";
    if (statusFilter !== "active") p.status = statusFilter;
    if (debouncedCode) p.code = debouncedCode;
    if (debouncedName) p.name = debouncedName;
    if (debouncedCity) p.city = debouncedCity;
    if (ddmFilter) p.ddm_migrated = ddmFilter === "yes";
    if (siteStatusFilter) p.site_status = siteStatusFilter;
    return p;
  };

  const fetchData = async (opts = {}) => {
    if (opts.all) {
      const { data } = await client.get(`/dealers`, { params: buildParams({ all: true }) });
      return data;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const { data } = await client.get(`/dealers`, {
        params: buildParams({ limit: BATCH_SIZE, offset: 0 }),
      });
      if (seq !== requestSeq.current) return data;
      setItems(data.items);
      setTotal(data.total);
      setOffset(data.items.length);
      setHasMore(data.items.length < data.total);
      return data;
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || loading || !hasMore) return;
    const seq = requestSeq.current;
    setLoadingMore(true);
    try {
      const { data } = await client.get(`/dealers`, {
        params: buildParams({ limit: BATCH_SIZE, offset }),
      });
      if (seq !== requestSeq.current) return;
      setItems((prev) => [...prev, ...data.items]);
      setTotal(data.total);
      const newOffset = offset + data.items.length;
      setOffset(newOffset);
      setHasMore(data.items.length > 0 && newOffset < data.total);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  useEffect(() => {
    client.get(`/activations`).then((r) => setActivations(r.data)).catch(() => {});
    client.get(`/dealers/tag-options`).then((r) => setTagOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchData().catch(() => {});
    setSelected(new Set());
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (predefined === "missing-coords") p.set("missing_coords", "1");
    if (predefined === "activation" && activationId) p.set("activation_id", activationId);
    if (selectedServices.length > 0) p.set("service_types", selectedServices.join(","));
    if (selectedTags.length > 0) p.set("tags", selectedTags.join(","));
    if (selectedTags.length > 1 && tagMatchMode === "any") p.set("tags_match", "any");
    if (statusFilter !== "active") p.set("status", statusFilter);
    setParams(p, { replace: true });
  }, [q, predefined, activationId, selectedServices, selectedTags, tagMatchMode, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort and column-filter changes refetch too, but are not mirrored into the
  // URL and keep the bulk selection (rows don't change identity).
  const isFirstColumnQuery = useRef(true);
  useEffect(() => {
    if (isFirstColumnQuery.current) {
      isFirstColumnQuery.current = false;
      return;
    }
    fetchData().catch(() => {});
  }, [sort, debouncedCode, debouncedName, debouncedCity, ddmFilter, siteStatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const cityOf = (b) =>
    (b?.city && (b.city["de-DE"] || Object.values(b.city)[0])) || "";

  // The server filters on one service type only; a multi-service AND-filter
  // is still narrowed down here.
  const rows = useMemo(() => {
    if (selectedServices.length <= 1) return items;
    return items.filter((d) =>
      selectedServices.every((s) => d.service_types.includes(s)),
    );
  }, [items, selectedServices]);

  const showSentinel = !loading && rows.length > 0;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current();
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [showSentinel]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag],
    );
  };

  const toggleSort = (col) => {
    setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }));
  };

  const toggleDdmMigrated = async (dealer) => {
    const nextVal = !dealer.management_migrated_to_ddm;
    try {
      await client.patch(`/dealers/${dealer.id}/ddm-migration`, { migrated: nextVal });
      setItems((prev) => prev.map((d) => (d.id === dealer.id ? { ...d, management_migrated_to_ddm: nextVal } : d)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update DDM migration flag");
    }
  };

  const copyCodes = async () => {
    try {
      // If user has made a bulk selection, copy just those. Otherwise copy the full filtered set.
      let codes;
      if (selected.size > 0) {
        codes = rows.filter((d) => selected.has(d.id)).map((d) => d.global_id);
      } else {
        const data = await fetchData({ all: true });
        let full = data.items;
        if (selectedServices.length > 1) {
          full = full.filter((d) =>
            selectedServices.every((s) => d.service_types.includes(s)),
          );
        }
        codes = full.map((d) => d.global_id);
      }
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success(`${codes.length} dealer codes copied`);
    } catch {
      toast.error("Failed to copy dealer codes");
    }
  };

  const clearAll = () => {
    setQ("");
    setPredefined("none");
    setActivationId("");
    setSelectedServices([]);
    setSelectedTags([]);
    setTagMatchMode("all");
    setStatusFilter("active");
    setCodeFilter("");
    setNameFilter("");
    setCityFilter("");
    setDdmFilter("");
    setSiteStatusFilter("");
  };

  const anyColumnFilterActive =
    !!codeFilter || !!nameFilter || !!cityFilter || !!ddmFilter || !!siteStatusFilter;

  const allSelectedOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelectedOnPage = rows.some((r) => selected.has(r.id)) && !allSelectedOnPage;

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        rows.forEach((r) => next.delete(r.id));
      } else {
        rows.forEach((r) => next.add(r.id));
      }
      return next;
    });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col p-6 md:p-10" data-testid="dealers-page">
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="data-label mb-2">{t("dashboard.title").toUpperCase()} · LIST</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("dealers.title")}</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("dealers.resultsCount", rows.length)} · {t("common.total").toLowerCase()} {total}
            {selected.size > 0 && (
              <>
                {" "}
                · <span className="font-medium text-foreground" data-testid="dealers-selected-count">
                  {selected.size} selected
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selected.size > 0 && (
            <Button
              data-testid="dealers-clear-selection"
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
              className="gap-1.5"
            >
              <X className="h-3.5 w-3.5" />
              Clear selection
            </Button>
          )}
          <Button
            data-testid="dealers-copy-codes"
            variant="outline"
            className="gap-2"
            onClick={copyCodes}
            disabled={loading}
          >
            <Copy className="h-4 w-4" />
            {selected.size > 0 ? `Copy ${selected.size} codes` : t("dealers.copyCodes")}
          </Button>
          {canBulkUpdate && (
            <Button
              data-testid="dealers-bulk-update"
              variant="outline"
              className="gap-2"
              onClick={() => {
                const dealerCodes =
                  selected.size > 0 ? rows.filter((d) => selected.has(d.id)).map((d) => d.global_id) : [];
                navigate("/dealers/bulk-update", { state: { dealerCodes } });
              }}
            >
              <PencilLine className="h-4 w-4" />
              {t("dealers.bulkUpdateButton")}
            </Button>
          )}
        </div>
      </div>

      {/* Top-level filters row */}
      <DealersToolbar
        q={q}
        setQ={setQ}
        predefined={predefined}
        setPredefined={setPredefined}
        activationId={activationId}
        setActivationId={setActivationId}
        activations={activations}
        selectedServices={selectedServices}
        selectedTags={selectedTags}
        toggleTag={toggleTag}
        tagOptions={tagOptions}
        tagMatchMode={tagMatchMode}
        setTagMatchMode={setTagMatchMode}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        anyColumnFilterActive={anyColumnFilterActive}
        onClearAll={clearAll}
      />

      {/* Table */}
      <Card className="flex flex-1 flex-col overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="dealers-table">
            <thead className="thead">
              <tr>
                <th className="w-10 px-3 py-2">
                  <Checkbox
                    data-testid="dealers-select-all"
                    checked={allSelectedOnPage}
                    // shadcn Checkbox supports "indeterminate" via data attr; we render as unchecked with visual dot instead if partial
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all"
                  />
                  {someSelectedOnPage && (
                    <span className="ml-1 inline-block h-1 w-1 rounded-full bg-primary align-middle" />
                  )}
                </th>
                {[
                  { key: "code", label: t("dealers.columns.code"), filterId: "code" },
                  { key: "name", label: t("dealers.columns.name"), filterId: "name" },
                  { key: "city", label: t("dealers.columns.city"), filterId: "city" },
                ].map((col) => (
                  <th key={col.key} className="whitespace-nowrap px-4 py-2 text-left">
                    <div className="flex items-center gap-1.5">
                      <button
                        data-testid={`sort-${col.key}`}
                        onClick={() => toggleSort(col.key)}
                        className="inline-flex items-center gap-1 hover:text-foreground"
                      >
                        {col.label}
                        <SortIndicator active={sort.col === col.key} dir={sort.dir} />
                      </button>
                      {col.filterId === "code" && (
                        <ColumnFilter
                          label="Filter code"
                          active={!!codeFilter}
                          testId="col-filter-code"
                        >
                          <p className="data-label mb-2">Filter by code</p>
                          <Input
                            data-testid="col-filter-code-input"
                            autoFocus
                            value={codeFilter}
                            onChange={(e) => setCodeFilter(e.target.value)}
                            placeholder="107-…"
                            className="font-mono text-xs"
                          />
                        </ColumnFilter>
                      )}
                      {col.filterId === "name" && (
                        <ColumnFilter
                          label="Filter name"
                          active={!!nameFilter}
                          testId="col-filter-name"
                        >
                          <p className="data-label mb-2">Filter by name</p>
                          <Input
                            data-testid="col-filter-name-input"
                            autoFocus
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            placeholder="Autohaus…"
                          />
                        </ColumnFilter>
                      )}
                      {col.filterId === "city" && (
                        <ColumnFilter
                          label="Filter city"
                          active={!!cityFilter}
                          testId="col-filter-city"
                        >
                          <p className="data-label mb-2">Filter by city</p>
                          <Input
                            data-testid="col-filter-city-input"
                            autoFocus
                            value={cityFilter}
                            onChange={(e) => setCityFilter(e.target.value)}
                            placeholder="Berlin…"
                          />
                        </ColumnFilter>
                      )}
                    </div>
                  </th>
                ))}
                <th className="whitespace-nowrap px-4 py-2 text-left">{t("dealers.columns.tags")}</th>
                <th className="whitespace-nowrap px-4 py-2 text-left">
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid="sort-ddm_migrated"
                      onClick={() => toggleSort("ddm_migrated")}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      DDM Migrated
                      <SortIndicator active={sort.col === "ddm_migrated"} dir={sort.dir} />
                    </button>
                    <ColumnFilter label="Filter DDM migrated" active={!!ddmFilter} testId="col-filter-ddm_migrated">
                      <p className="data-label mb-2">Filter by DDM migrated</p>
                      <ChoiceFilter
                        testId="col-filter-ddm_migrated-option"
                        value={ddmFilter}
                        onChange={setDdmFilter}
                        options={[
                          { value: "yes", label: "Migrated" },
                          { value: "no", label: "Not migrated" },
                        ]}
                      />
                    </ColumnFilter>
                  </div>
                </th>
                <th className="min-w-[160px] whitespace-nowrap px-4 py-2 text-left">
                  <div className="flex items-center gap-1.5">
                    <button
                      data-testid="sort-site_status"
                      onClick={() => toggleSort("site_status")}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                    >
                      {t("dealership.siteStatus.title")}
                      <SortIndicator active={sort.col === "site_status"} dir={sort.dir} />
                    </button>
                    <ColumnFilter label="Filter site status" active={!!siteStatusFilter} testId="col-filter-site_status">
                      <p className="data-label mb-2">{t("dealership.siteStatus.title")}</p>
                      <ChoiceFilter
                        testId="col-filter-site_status-option"
                        value={siteStatusFilter}
                        onChange={setSiteStatusFilter}
                        options={SITE_STATUSES.map((v) => ({ value: v, label: t(`dealership.siteStatus.${v}`) }))}
                      />
                    </ColumnFilter>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                ["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => (
                  <tr key={k} className="border-t border-border">
                    <td colSpan={7} className="px-4 py-2">
                      <Skeleton className="h-6 w-full" />
                    </td>
                  </tr>
                ))}

              {!loading && rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-16 text-center text-sm text-muted-foreground"
                    data-testid="dealers-empty"
                  >
                    {t("dealers.noResults")}
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((d) => {
                  const b = d.primary_branch || {};
                  const city = cityOf(b);
                  const isSel = selected.has(d.id);
                  return (
                    <tr
                      key={d.id}
                      className={`row-hover cursor-pointer border-t border-border ${isSel ? "bg-primary/5" : ""} ${d.deleted ? "text-muted-foreground line-through" : ""}`}
                      data-testid={`dealer-row-${d.id}`}
                      onClick={openDealerOnClick(navigate, d.id)}
                      onAuxClick={openDealerOnClick(navigate, d.id)}
                    >
                      <td
                        className="w-10 px-3 py-2"
                        onClick={(e) => e.stopPropagation()}
                        onAuxClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          data-testid={`dealer-select-${d.id}`}
                          checked={isSel}
                          onCheckedChange={() => toggleOne(d.id)}
                        />
                      </td>
                      <td className={`whitespace-nowrap px-4 py-2 font-mono text-xs tabular-nums ${d.deleted ? "line-through" : ""}`}>
                        {d.global_id}
                      </td>
                      <td
                        className={`whitespace-nowrap px-4 py-2 font-medium ${d.deleted ? "line-through" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                        onAuxClick={(e) => e.stopPropagation()}
                      >
                        <Link to={`/dealers/${d.id}`} className="hover:underline">
                          {d.name}
                        </Link>
                      </td>
                      <td className={`px-4 py-2 text-muted-foreground ${d.deleted ? "line-through" : ""}`}>{city}</td>
                      <td className="px-4 py-2" data-testid={`dealer-tags-${d.id}`}>
                        <DealerTagBadges tags={d.tags} />
                      </td>
                      <td
                        className="px-4 py-2"
                        onClick={(e) => e.stopPropagation()}
                        onAuxClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          data-testid={`dealer-ddm-migrated-${d.id}`}
                          checked={d.management_migrated_to_ddm === true}
                          onCheckedChange={() => toggleDdmMigrated(d)}
                          aria-label="Management migrated to DDM"
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2" data-testid={`dealer-site-status-${d.id}`}>
                        <SiteStatusBadge dealer={d} t={t} />
                      </td>
                    </tr>
                  );
                })}

              {showSentinel && (
                <tr>
                  <td colSpan={7} ref={sentinelRef} className="px-4 py-3 text-center text-xs text-muted-foreground">
                    {loadingMore
                      ? "Loading more…"
                      : hasMore
                        ? ""
                        : `All ${items.length} matching dealer${items.length === 1 ? "" : "s"} loaded`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
